import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  type SimpleChanges,
} from '@angular/core';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';

export type DateSelectOutput = {
  fromDate: string;
  toDate: string;
};

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
})
export class DateRangePickerComponent implements OnInit, OnChanges {
  @Input() label: string = '';

  @Input() value: string | null = null;

  @Input() disablePastDates: boolean = true;

  @Input() maxSelectableDate: NgbDateStruct | null = null;

  @Input() maxRangeMonths: number | null = null;

  @Input() disabled: boolean = false;

  @Input() tooltip: string = '';

  @Output() dateSelect = new EventEmitter<DateSelectOutput>();

  calendar = inject(NgbCalendar);
  formatter = inject(NgbDateParserFormatter);

  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null = null;
  toDate: NgbDate | null = null;
  minDate: NgbDate | null = null;
  maxDate: NgbDate | null = null;
  private _lastEmittedFrom: string | null = null;
  private _lastEmittedTo: string | null = null;

  get displayStartDate(): NgbDate {
    if (this.fromDate) {
      return new NgbDate(this.fromDate.year, this.fromDate.month, 1);
    }
    const today = this.calendar.getToday();
    return new NgbDate(today.year, today.month, 1);
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('value' in changes) {
      const value = changes?.value?.currentValue;

      if (value) {
        // Verifica se o valor é um range no formato "YYYY-MM-DD-YYYY-MM-DD"
        if (typeof value === 'string' && value.includes('-')) {
          const parts = value.split('-');

          // Se temos exatamente 6 partes, é um range (YYYY-MM-DD-YYYY-MM-DD)
          if (parts.length === 6) {
            const fromDateStr = `${parts[0]}-${parts[1]}-${parts[2]}`;
            const toDateStr = `${parts[3]}-${parts[4]}-${parts[5]}`;

            this.fromDate = this.formatter.parse(fromDateStr) as NgbDate;
            this.toDate = this.formatter.parse(toDateStr) as NgbDate;
          } else if (parts.length === 3) {
            // Se temos 3 partes, é uma data única (YYYY-MM-DD)
            this.fromDate = this.formatter.parse(value) as NgbDate;
            this.toDate = null;
          }
        } else {
          // Tenta fazer o parse normal
          this.fromDate = this.formatter.parse(value) as NgbDate;
          this.toDate = null;
        }
      } else {
        this.fromDate = null;
        this.toDate = null;
      }
    }

    if (
      'disablePastDates' in changes ||
      'maxSelectableDate' in changes
    ) {
      this.setMinDate();
      this.setMaxDate();
    }
  }

  ngOnInit() {
    this.setMinDate();
    this.setMaxDate();
  }

  onDateSelectionFinished() {
    if (this.disabled) return;
    const fromStr = this.formatter.format(this.fromDate);
    const toStr = this.formatter.format(this.toDate);

    // Evita emissão duplicada (quando já emitido no onDateSelection)
    if (fromStr === this._lastEmittedFrom && toStr === this._lastEmittedTo) {
      return;
    }

    this._lastEmittedFrom = fromStr;
    this._lastEmittedTo = toStr;
    this.dateSelect.emit({ fromDate: fromStr, toDate: toStr });
  }

  onDateSelection(date: NgbDate) {
    if (this.disabled) return;
    // Não permite selecionar datas no passado quando desabilitado
    if (this.disablePastDates && this.minDate && date && date.before(this.minDate)) {
      return;
    }

    if (this.maxDate && date && date.after(this.maxDate)) {
      return;
    }

    if (this.isOutsideMaxRange(date)) {
      return;
    }

    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
      this._lastEmittedFrom = null;
      this._lastEmittedTo = null;
    } else if (this.fromDate && !this.toDate && date) {
      if (date.after(this.fromDate) || date.equals(this.fromDate)) {
        this.toDate = date;
        this.onDateSelectionFinished();
      } else {
        this.fromDate = date;
      }
    } else {
      this.toDate = null;
      this.fromDate = date;
      this._lastEmittedFrom = null;
      this._lastEmittedTo = null;
    }
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  /**
   * Verifica se uma data está no passado
   */
  isPastDate(date: NgbDate): boolean {
    if (!this.disablePastDates || !date || !this.minDate) {
      return false;
    }
    return date.before(this.minDate);
  }

  isFutureDate(date: NgbDate): boolean {
    if (!date || !this.maxDate) {
      return false;
    }

    return date.after(this.maxDate);
  }

  isDisabledDate(date: NgbDate): boolean {
    return this.isPastDate(date) || this.isFutureDate(date) || this.isOutsideMaxRange(date);
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    if (this.disabled) return currentValue;
    const parsed = this.formatter.parse(input);
    if (parsed && this.calendar.isValid(NgbDate.from(parsed))) {
      const date = NgbDate.from(parsed);
      if (!date) {
        return currentValue;
      }

      // Não permite datas no passado
      if (this.disablePastDates && this.minDate && date.before(this.minDate)) {
        return currentValue;
      }

      if (this.maxDate && date.after(this.maxDate)) {
        return currentValue;
      }

      if (this.isOutsideMaxRange(date)) {
        return currentValue;
      }

      return date;
    }
    return currentValue;
  }

  getDateText() {
    if (!this.fromDate) return '';

    const fromDate = moment(this.formatter.format(this.fromDate)).format('DD/MM/YYYY');

    if (this.toDate) {
      const toDate = moment(this.formatter.format(this.toDate)).format('DD/MM/YYYY');
      return `${fromDate} - ${toDate}`;
    }

    return `${fromDate}`;
  }

  clear() {
    if (this.disabled) return;
    this.fromDate = null;
    this.toDate = null;
    this.hoveredDate = null;
    this._lastEmittedFrom = null;
    this._lastEmittedTo = null;
    this.dateSelect.emit({
      fromDate: '',
      toDate: '',
    });
  }

  private setMinDate(): void {
    this.minDate = this.disablePastDates ? this.calendar.getToday() : null;
  }

  private setMaxDate(): void {
    this.maxDate = this.maxSelectableDate ? NgbDate.from(this.maxSelectableDate) : null;
  }

  private isOutsideMaxRange(date: NgbDate): boolean {
    if (!this.maxRangeMonths || this.maxRangeMonths <= 0 || !this.fromDate || this.toDate || !date) {
      return false;
    }

    const rangeEnd = this.calendar.getNext(this.fromDate, 'm', this.maxRangeMonths);
    return date.after(rangeEnd);
  }
}
